import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Heart, Activity, TrendingUp, Plus, FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Health = () => {
  const { API } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [formData, setFormData] = useState({
    glucose: '',
    cholesterol: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
  });

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/health/metrics`);
      setMetrics(response.data.metrics || []);
      setThresholds(response.data.thresholds || {});
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/health/export-pdf/generate`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_medical_nutrihealth_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Raportul PDF a fost descărcat cu succes!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Eroare la generarea raportului PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/health/metrics`, {
        glucose: parseFloat(formData.glucose) || null,
        cholesterol: parseFloat(formData.cholesterol) || null,
        blood_pressure_systolic: parseFloat(formData.blood_pressure_systolic) || null,
        blood_pressure_diastolic: parseFloat(formData.blood_pressure_diastolic) || null,
      });

      toast.success('Metrici adăugate cu succes!');
      setFormData({ glucose: '', cholesterol: '', blood_pressure_systolic: '', blood_pressure_diastolic: '' });
      fetchMetrics();
    } catch (error) {
      toast.error('Eroare la adăugarea metricilor');
    } finally {
      setLoading(false);
    }
  };

  const chartData = metrics.slice(0, 10).reverse().map((m) => ({
    date: new Date(m.date).toLocaleDateString('ro-RO', { month: 'short', day: 'numeric' }),
    glucose: m.glucose,
    cholesterol: m.cholesterol,
  }));

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8 max-w-7xl" data-testid="health-page">
        <div className="animate-fade-in">
          <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
                Monitorizare Sănătate
              </h1>
              <p className="text-[#4a5568] text-base md:text-lg">
                Urmărește-ți metricile de sănătate cu praguri OMS
              </p>
            </div>
            <Button
              onClick={handleExportPdf}
              disabled={exportingPdf}
              data-testid="export-pdf-button"
              className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] flex items-center gap-2"
            >
              {exportingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {exportingPdf ? 'Se generează...' : 'Export Raport Medical PDF'}
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Add Metrics Form */}
            <Card className="lg:col-span-1 border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Plus className="w-6 h-6 mr-2" />
                  Adăugă Metrici
                </CardTitle>
                <CardDescription>Introdu noile măsurători</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="glucose">Glicemie (mg/dL)</Label>
                    <Input
                      id="glucose"
                      data-testid="glucose-input"
                      type="number"
                      step="0.1"
                      placeholder="Normal: 70-100"
                      value={formData.glucose}
                      onChange={(e) => setFormData({ ...formData, glucose: e.target.value })}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cholesterol">Colesterol (mg/dL)</Label>
                    <Input
                      id="cholesterol"
                      data-testid="cholesterol-input"
                      type="number"
                      step="0.1"
                      placeholder="Normal: <200"
                      value={formData.cholesterol}
                      onChange={(e) => setFormData({ ...formData, cholesterol: e.target.value })}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="systolic">Sistolică</Label>
                      <Input
                        id="systolic"
                        data-testid="systolic-input"
                        type="number"
                        placeholder="120"
                        value={formData.blood_pressure_systolic}
                        onChange={(e) => setFormData({ ...formData, blood_pressure_systolic: e.target.value })}
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="diastolic">Diastolică</Label>
                      <Input
                        id="diastolic"
                        data-testid="diastolic-input"
                        type="number"
                        placeholder="80"
                        value={formData.blood_pressure_diastolic}
                        onChange={(e) => setFormData({ ...formData, blood_pressure_diastolic: e.target.value })}
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="add-metrics-button"
                    className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                  >
                    {loading ? 'Se adaugă...' : 'Adăugă Metrici'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Charts */}
            <Card className="lg:col-span-2 border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Activity className="w-6 h-6 mr-2" />
                  Grafice Sănătate
                </CardTitle>
                <CardDescription>Evoluția metricilor în timp</CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="space-y-8">
                    <div>
                      <p className="text-sm font-medium text-[#4a5568] mb-2">Glicemie</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" stroke="#4a5568" />
                          <YAxis stroke="#4a5568" />
                          <Tooltip />
                          <Line type="monotone" dataKey="glucose" stroke="#1A4D2E" strokeWidth={2} dot={{ fill: '#1A4D2E' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-[#4a5568] mb-2">Colesterol</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" stroke="#4a5568" />
                          <YAxis stroke="#4a5568" />
                          <Tooltip />
                          <Line type="monotone" dataKey="cholesterol" stroke="#9BCF53" strokeWidth={2} dot={{ fill: '#9BCF53' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Heart className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#4a5568]">Nu există date pentru grafice. Adăugă primele metrici!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Thresholds Reference */}
          <Card className="border-none card-shadow">
            <CardHeader>
              <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                <TrendingUp className="w-6 h-6 mr-2" />
                Praguri OMS (Organizația Mondială a Sănătății)
              </CardTitle>
              <CardDescription>Valorile normale pentru o sănătate optimă</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(thresholds).map(([key, value]) => (
                  <div key={key} className="bg-[#F5F5F0] p-4 rounded-xl">
                    <p className="text-sm font-medium text-[#4a5568] mb-1">{value.name}</p>
                    <p className="text-lg font-semibold text-[#1A4D2E]">
                      {value.min} - {value.max} {value.unit}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Health;